import type { ApiError } from '@/types/api'

// Configuration de l'API
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string

// Classe d'erreur personnalisée pour l'API
export class ApiHttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: ApiError
  ) {
    super(message)
    this.name = 'ApiHttpError'
  }
}

// Fonction utilitaire pour construire les URLs avec paramètres
export function buildUrl(
  endpoint: string,
  params?: Record<string, string | number | undefined>
): string {
  const url = `${API_BASE_URL}${endpoint}`

  if (!params) return url

  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.set(key, String(value))
    }
  })

  return searchParams.toString() ? `${url}?${searchParams}` : url
}

// Fonction utilitaire pour gérer les réponses
export async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorData: ApiError | { message: string }

    try {
      errorData = await response.json()
    } catch {
      // Si le JSON est invalide, on crée une erreur de base
      throw new ApiHttpError(response.statusText, response.status, {
        success: false,
        error: { code: `HTTP_${response.status}`, message: response.statusText },
      })
    }

    const errorMessage =
      'error' in errorData && typeof errorData.error === 'string'
        ? errorData.error
        : 'error' in errorData && typeof errorData.error === 'object' && errorData.error !== null
          ? errorData.error.message
          : 'message' in errorData
            ? errorData.message
            : 'Une erreur de communication est survenue.'

    // Si erreur 401 (token invalide/expiré), déconnecter l'utilisateur
    if (response.status === 401) {
      console.log('[API] Token invalide ou expiré, déconnexion automatique')
      // Import dynamique pour éviter les dépendances circulaires
      import('@/stores/auth').then(({ useAuthStore }) => {
        const authStore = useAuthStore()
        authStore.logout()
        // Rediriger vers la page de login si on est dans le navigateur
        if (typeof window !== 'undefined') {
          window.location.href = '/login'
        }
      })
    }

    throw new ApiHttpError(errorMessage, response.status, errorData as ApiError)
  }

  // Pour les réponses 204 (No Content), il n'y a pas de body
  if (response.status === 204) {
    return undefined as T
  }

  // Pour les réponses de succès, on attend une structure { success: true, data: ... }
  // ou directement les données. Le service appelant doit connaître la structure attendue.
  // Vérifier si le body existe avant de parser
  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    const text = await response.text()
    return text ? JSON.parse(text) : (undefined as T)
  }

  return undefined as T
}

// Fonction pour construire les en-têtes avec le token d'authentification
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
  const token = localStorage.getItem('accessToken')
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

// Fonction utilitaire pour faire des requêtes GET
export async function apiGet<T>(
  endpoint: string,
  params?: Record<string, string | number | undefined>
): Promise<T> {
  const url = buildUrl(endpoint, params)
  //console.log(`[API] GET ${url}`)

  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: getAuthHeaders(),
    })

    /*console.log(`[API] Response status for ${endpoint}:`, response.status, response.statusText)
    console.log(
      `[API] Response headers for ${endpoint}:`,
      Object.fromEntries(response.headers.entries())
    )*/

    const data = await handleResponse<T>(response)

    // console.log(`[API] Response for GET ${endpoint}:`, {
    //   status: response.status,
    //   dataLength: Array.isArray((data as any)?.data) ? (data as any).data.length : 'N/A',
    // })

    return data
  } catch (error) {
    console.error(`[API] Error for ${endpoint}:`, {
      description: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      overflow: false,
      properties:
        error instanceof Error
          ? Object.getOwnPropertyNames(error).map(name => ({
              name,
              type: typeof (error as any)[name],
              value: String((error as any)[name]).substring(0, 100),
            }))
          : [],
    })
    throw error
  }
}

// Fonction utilitaire pour faire des requêtes POST/PUT
export async function apiPost<T>(
  endpoint: string,
  data?: unknown,
  method: 'POST' | 'PUT' = 'POST'
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  //console.log(`[API] ${method} ${url}`)

  try {
    const response = await fetch(url, {
      method,
      credentials: 'include',
      headers: getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    })

    const result = await handleResponse<T>(response)

    /*console.log(`[API] Response for ${endpoint}:`, {
      status: response.status,
    })*/

    return result
  } catch (error) {
    console.error(`[API] Error for ${endpoint}:`, error)
    throw error
  }
}

/**
 * Simplification pour les requêtes PUT.
 * @param endpoint L'endpoint de l'API.
 * @param data Les données à envoyer.
 * @returns La réponse de l'API.
 */
export function apiPut<T>(
  endpoint: string,
  data: unknown,
  params?: Record<string, string | number | undefined>
): Promise<T> {
  // Construire l'endpoint avec les paramètres de requête
  let finalEndpoint = endpoint
  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.set(key, String(value))
      }
    })
    const queryString = searchParams.toString()
    if (queryString) {
      finalEndpoint = `${endpoint}?${queryString}`
    }
  }
  return apiPost<T>(finalEndpoint, data, 'PUT')
}

// Fonction utilitaire pour faire des requêtes DELETE
export async function apiDelete<T>(endpoint: string): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  console.log(`[API] DELETE ${url}`)

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers: getAuthHeaders(),
    })

    return handleResponse<T>(response)
  } catch (error) {
    console.error(`[API] Error for ${endpoint}:`, error)
    throw error
  }
}

export function apiUrl(path: string) {
  // Gère les slashs pour éviter les // ou oublis
  return `${API_BASE_URL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`
}
